// Estado del alta guiada (consulta progresiva, resultados y activación)
// fuera del componente, para que sobreviva a la navegación dentro de la SPA.
//
// La consulta a la Rama Judicial corre en el backend: aquí se crea, se
// sondea el progreso y se leen los resultados ya guardados (20 por página).
// Si la consulta termina mientras la persona está en otra sección, se avisa
// con una notificación global; al volver, todo sigue tal cual.

import { showNotification } from '../../errorBanner.svelte';
import { getRoute } from '../../route.svelte';
import * as api from './api';
import type {
  ActuacionConsulta,
  ActivacionInfo,
  ConsultaInfo,
  CrearSeguimientoResponse,
  EstadoConsultaResponse,
} from './types';

export type TipoAlta = 'radicado' | 'nombre';
export type OrdenConsulta = 'estable' | 'actuacion';

export const RESULTADOS_POR_PAGINA = 20;

export type ExpansionPreview = {
  idProceso: number;
  loading: boolean;
  error: string | null;
  pagina: number;
  paginas: number;
  total: number;
  cargandoMas: boolean;
  actuaciones: ActuacionConsulta[];
};

export const alta = $state({
  tipo: null as TipoAlta | null,
  valor: '',
  correoActivo: true,

  // Consulta progresiva.
  consulta: null as ConsultaInfo | null,
  consultaLoading: false,
  consultaError: null as string | null,
  consultaMensaje: null as string | null,

  // Resultados ya guardados (página actual).
  resultados: [] as import('./types').ProcesoConsulta[],
  resultadosPagina: 1,
  resultadosTotal: 0,
  resultadosLoading: false,
  resultadosError: null as string | null,
  orden: 'estable' as OrdenConsulta,

  // Activación del seguimiento.
  activacion: null as ActivacionInfo | null,
  activarBusy: false,
  activarError: null as string | null,
  activarMensaje: null as string | null,
  creado: null as CrearSeguimientoResponse | null,

  // Seguir un proceso concreto desde un resultado.
  seguido: null as CrearSeguimientoResponse | null,
  seguirBusy: false,
  seguirError: null as string | null,

  // Expansión de actuaciones de un resultado.
  expansion: null as ExpansionPreview | null,
});

// Cada consulta/activación nueva invalida la anterior: sólo la última
// respuesta en vuelo puede escribir el estado.
let secuenciaConsulta = 0;
let secuenciaResultados = 0;
let secuenciaActivacion = 0;
let secuenciaSeguir = 0;
let secuenciaEstado = 0;
let ultimoEstadoAplicado = 0;
let actualizarResultadosPendiente = false;

// Sondeo del progreso: frecuente con el módulo visible, espaciado fuera.
const POLL_MS = 2000;
const POLL_OCULTO_CADA = 5;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollLatido = 0;

// Evita repetir el aviso de fin o de activación completada.
let notificadoEstado: string | null = null;
let activacionNotificada: number | null = null;

function enModulo(): boolean {
  const nombre = getRoute().name;
  return nombre === 'seguimiento' || nombre === 'seguimiento-detalle';
}

function avisar(mensaje: string, tipo: 'success' | 'error'): void {
  if (!enModulo()) {
    showNotification(mensaje, tipo, 8000);
  }
}

// ── Sondeo del progreso ─────────────────────────────────────

function detenerPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function iniciarPolling(): void {
  if (pollTimer !== null) return;
  pollTimer = setInterval(() => {
    pollLatido += 1;
    if (!enModulo() && pollLatido % POLL_OCULTO_CADA !== 0) return;
    void sondearEstado();
  }, POLL_MS);
}

function necesitaSondeo(): boolean {
  const consulta = alta.consulta;
  return Boolean(consulta && (consulta.en_curso || alta.activacion?.pendiente));
}

export async function sondearEstado(): Promise<void> {
  const consulta = alta.consulta;
  if (!consulta || !necesitaSondeo() || alta.activarBusy || alta.consultaLoading) return;
  const generacion = secuenciaConsulta;
  const activacion = secuenciaActivacion;
  const secuencia = ++secuenciaEstado;
  try {
    const respuesta = await api.estadoConsulta(consulta.id);
    if (generacion !== secuenciaConsulta || activacion !== secuenciaActivacion) return;
    if (alta.consulta?.id !== consulta.id) return;
    if (secuencia < ultimoEstadoAplicado) return;
    ultimoEstadoAplicado = secuencia;
    aplicarEstado(respuesta);
  } catch {
    // El sondeo falla en silencio: el siguiente latido reintenta.
  }
}

function aplicarEstado(respuesta: EstadoConsultaResponse): void {
  const anterior = alta.consulta;
  const siguiente = respuesta.consulta;
  alta.consulta = siguiente;
  alta.activacion = respuesta.activacion;

  if (siguiente.en_curso && anterior && !anterior.en_curso) {
    // La recuperación reencoló una consulta interrumpida.
    iniciarPolling();
  }

  // Una página visible a medias se completa en silencio cuando llegan
  // más resultados, sin mover lo que ya se está leyendo. Si todavía no hay
  // nada cargado, la primera página aparece apenas exista.
  const crecio = anterior !== null && siguiente.resultados > anterior.resultados;
  const reiniciada = anterior !== null && (
    (siguiente.reinicios ?? 0) !== (anterior.reinicios ?? 0)
    || siguiente.resultados < anterior.resultados
    || siguiente.paginas_leidas < anterior.paginas_leidas
  );
  if (reiniciada) {
    // La fuente cambió durante la recuperación: las páginas guardadas ya no
    // pertenecen al mismo recorrido, aunque el ID de consulta sea el mismo.
    secuenciaResultados += 1;
    actualizarResultadosPendiente = false;
    alta.resultados = [];
    alta.resultadosPagina = 1;
    alta.resultadosTotal = 0;
    alta.resultadosLoading = false;
    alta.resultadosError = null;
    alta.orden = 'estable';
    alta.expansion = null;
    if (siguiente.resultados > 0) void cargarResultados(1);
  } else if (crecio && alta.resultadosLoading) {
    actualizarResultadosPendiente = true;
  } else if (crecio) {
    if (alta.resultados.length === 0 && siguiente.resultados > 0) {
      void cargarResultados(1);
    } else if (
      alta.resultados.length > 0
      && alta.resultados.length < RESULTADOS_POR_PAGINA
    ) {
      void cargarResultados(alta.resultadosPagina, { silencioso: true });
    }
  }

  avisarTransicion(siguiente);
  avisarActivacion(siguiente);

  if (!necesitaSondeo()) detenerPolling();
}

function avisarTransicion(consulta: ConsultaInfo): void {
  if (consulta.en_curso) return;
  const clave = `${consulta.id}:${consulta.estado}`;
  if (notificadoEstado === clave) return;
  notificadoEstado = clave;
  if (consulta.estado === 'completa') {
    const total = consulta.resultados;
    avisar(
      `Consulta de «${consulta.etiqueta}» terminada: ${total} `
        + (total === 1 ? 'resultado listo.' : 'resultados listos.'),
      'success',
    );
  } else if (consulta.estado === 'parcial') {
    avisar(
      `La consulta de «${consulta.etiqueta}» quedó interrumpida: `
        + `${consulta.resultados} resultados disponibles.`,
      'error',
    );
  } else if (consulta.estado === 'fallida') {
    avisar(
      `La consulta de «${consulta.etiqueta}» falló: `
        + `${consulta.error || 'error de la fuente.'}`,
      'error',
    );
  }
}

function avisarActivacion(consulta: ConsultaInfo): void {
  const activacion = alta.activacion;
  if (
    activacion?.completada
    && activacion.seguimiento_id
    && activacionNotificada !== activacion.seguimiento_id
  ) {
    activacionNotificada = activacion.seguimiento_id;
    avisar(`Tu seguimiento de «${consulta.etiqueta}» quedó activo.`, 'success');
  }
}

// ── Apertura y cierre del alta ──────────────────────────────

function limpiarConsulta(): void {
  secuenciaConsulta += 1;
  secuenciaResultados += 1;
  secuenciaActivacion += 1;
  actualizarResultadosPendiente = false;
  detenerPolling();
  alta.consulta = null;
  alta.consultaLoading = false;
  alta.consultaError = null;
  alta.consultaMensaje = null;
  alta.resultados = [];
  alta.resultadosPagina = 1;
  alta.resultadosTotal = 0;
  alta.resultadosLoading = false;
  alta.resultadosError = null;
  alta.orden = 'estable';
  alta.activacion = null;
  alta.activarBusy = false;
  alta.activarError = null;
  alta.activarMensaje = null;
  alta.creado = null;
  alta.expansion = null;
  alta.seguirError = null;
  notificadoEstado = null;
  activacionNotificada = null;
}

export function abrirAlta(tipo: TipoAlta): void {
  if (alta.activarBusy || alta.seguirBusy) return;

  const actual = alta.consulta;
  if (actual && actual.tipo === tipo) {
    // Se reabre la consulta en curso (o sus resultados) tal como estaba.
    alta.tipo = tipo;
    alta.valor = actual.etiqueta;
    if (necesitaSondeo()) iniciarPolling();
    return;
  }

  limpiarConsulta();
  alta.tipo = tipo;
  alta.valor = '';
  alta.correoActivo = true;

  // Tras recargar la página, retoma la consulta en curso o la activación
  // pendiente de este tipo (el backend es la fuente de verdad).
  const secuencia = secuenciaConsulta;
  alta.consultaLoading = true;
  void (async () => {
    try {
      const respuesta = await api.consultaReciente(tipo);
      if (secuencia !== secuenciaConsulta || alta.tipo !== tipo) return;
      const consulta = respuesta.consulta;
      if (!consulta) return;
      const recuperable = consulta.en_curso || Boolean(respuesta.activacion?.pendiente);
      if (!recuperable) return;
      alta.consulta = consulta;
      alta.activacion = respuesta.activacion;
      alta.valor = consulta.etiqueta;
      if (necesitaSondeo()) iniciarPolling();
      await cargarResultados(1);
    } catch {
      // Sin conexión o error puntual: el alta queda en blanco.
    } finally {
      if (secuencia === secuenciaConsulta) alta.consultaLoading = false;
    }
  })();
}

export function cerrarAlta(): void {
  if (alta.activarBusy || alta.seguirBusy) return;
  alta.tipo = null;
  alta.valor = '';
  alta.consultaMensaje = null;
  alta.consultaError = null;
  alta.expansion = null;
  alta.seguirError = null;
  // La consulta y su sondeo siguen en segundo plano: el aviso de fin (y de
  // activación completada) llega aunque el panel esté cerrado.
}

// ── Consulta ────────────────────────────────────────────────

export async function consultar(event?: SubmitEvent): Promise<void> {
  event?.preventDefault();
  if (alta.activarBusy || alta.seguirBusy) return;
  const tipo = alta.tipo;
  if (!tipo) return;
  const valor = alta.valor.trim();
  if (!valor) return;

  limpiarConsulta();
  const secuencia = secuenciaConsulta;
  alta.consultaLoading = true;
  try {
    const respuesta = await api.iniciarConsulta(tipo, valor);
    if (secuencia !== secuenciaConsulta) return;
    if (!respuesta.utilizable) {
      alta.consultaMensaje = respuesta.mensaje || 'Revisa el dato ingresado.';
      return;
    }
    if (!respuesta.consulta) return;
    alta.consulta = respuesta.consulta;
    if (necesitaSondeo()) iniciarPolling();
    if (respuesta.consulta.resultados > 0) void cargarResultados(1);
  } catch (error) {
    if (secuencia !== secuenciaConsulta) return;
    alta.consultaError = api.mensajeError(error);
  } finally {
    if (secuencia === secuenciaConsulta) alta.consultaLoading = false;
  }
}

export async function reintentarConsulta(): Promise<void> {
  const consulta = alta.consulta;
  if (!consulta || alta.consultaLoading) return;
  const secuencia = ++secuenciaConsulta;
  alta.consultaLoading = true;
  alta.consultaError = null;
  try {
    const respuesta = await api.reintentarConsulta(consulta.id);
    if (secuencia !== secuenciaConsulta) return;
    aplicarEstado({ consulta: respuesta.consulta, activacion: alta.activacion });
    if (necesitaSondeo()) iniciarPolling();
  } catch (error) {
    if (secuencia !== secuenciaConsulta) return;
    alta.consultaError = api.mensajeError(error);
  } finally {
    if (secuencia === secuenciaConsulta) alta.consultaLoading = false;
  }
}

// ── Resultados ──────────────────────────────────────────────

export function paginasDisponibles(): number {
  const total = Math.max(alta.consulta?.resultados ?? 0, alta.resultadosTotal);
  return Math.max(1, Math.ceil(total / RESULTADOS_POR_PAGINA));
}

export async function cargarResultados(
  pagina: number,
  opciones: { silencioso?: boolean } = {},
): Promise<void> {
  const consulta = alta.consulta;
  if (!consulta) return;
  const secuencia = ++secuenciaResultados;
  if (!opciones.silencioso) {
    alta.resultadosLoading = true;
    alta.resultadosError = null;
  }
  try {
    const respuesta = await api.resultadosConsulta(consulta.id, pagina, alta.orden);
    if (secuencia !== secuenciaResultados) return;
    if (alta.consulta?.id !== consulta.id) return;
    alta.resultados = respuesta.resultados;
    alta.resultadosPagina = respuesta.pagina;
    alta.resultadosTotal = respuesta.total;
  } catch (error) {
    if (secuencia !== secuenciaResultados) return;
    if (!opciones.silencioso) alta.resultadosError = api.mensajeError(error);
  } finally {
    if (!opciones.silencioso && secuencia === secuenciaResultados) {
      alta.resultadosLoading = false;
    }
    if (secuencia === secuenciaResultados && actualizarResultadosPendiente) {
      actualizarResultadosPendiente = false;
      void cargarResultados(alta.resultadosPagina, { silencioso: alta.resultados.length > 0 });
    }
  }
}

export function irPaginaResultados(delta: number): void {
  if (alta.resultadosLoading) return;
  const disponibles = paginasDisponibles();
  const actual = Math.min(alta.resultadosPagina, disponibles);
  const destino = Math.min(Math.max(actual + delta, 1), disponibles);
  if (destino === actual) return;
  void cargarResultados(destino);
}

export function cambiarOrden(orden: OrdenConsulta): void {
  if (alta.orden === orden) return;
  if (orden === 'actuacion' && alta.consulta?.estado !== 'completa') return;
  alta.orden = orden;
  alta.expansion = null;
  void cargarResultados(1);
}

// ── Activación del seguimiento ──────────────────────────────

export async function activarSeguimiento(): Promise<void> {
  const consulta = alta.consulta;
  if (!consulta || alta.activarBusy || alta.seguirBusy) return;
  if (consulta.estado === 'fallida' || consulta.estado === 'cancelada') return;
  const secuencia = ++secuenciaActivacion;
  alta.activarBusy = true;
  alta.activarError = null;
  alta.activarMensaje = null;
  try {
    const respuesta = await api.activarConsulta(consulta.id, alta.correoActivo);
    if (secuencia !== secuenciaActivacion) return;
    alta.activacion = {
      pendiente: respuesta.pendiente,
      completada: !respuesta.pendiente,
      seguimiento_id: respuesta.seguimiento.id,
    };
    if (respuesta.pendiente) {
      iniciarPolling();
    } else {
      alta.creado = {
        creado: respuesta.creado,
        seguimiento: respuesta.seguimiento,
      };
    }
  } catch (error) {
    if (secuencia !== secuenciaActivacion) return;
    alta.activarError = api.mensajeError(error);
  } finally {
    if (secuencia === secuenciaActivacion) alta.activarBusy = false;
  }
}

export async function cancelarActivacion(): Promise<void> {
  const activacion = alta.activacion;
  if (!activacion || activacion.completada || alta.activarBusy) return;
  if (!activacion.seguimiento_id) return;
  const secuencia = ++secuenciaActivacion;
  alta.activarBusy = true;
  alta.activarError = null;
  try {
    await api.eliminarSeguimiento(activacion.seguimiento_id, true);
    if (secuencia !== secuenciaActivacion) return;
    alta.activacion = null;
    alta.activarMensaje = 'Activación cancelada. La consulta sigue disponible.';
  } catch (error) {
    if (secuencia !== secuenciaActivacion) return;
    alta.activarError = api.mensajeError(error);
  } finally {
    if (secuencia === secuenciaActivacion) alta.activarBusy = false;
  }
}

// ── Expansión de un resultado ───────────────────────────────

export function toggleProcesoPreview(idProceso: number | null | undefined): void {
  if (!idProceso) return;
  const abierta = alta.expansion;
  if (abierta && abierta.idProceso === idProceso) {
    alta.expansion = null;
    return;
  }
  alta.expansion = {
    idProceso,
    loading: true,
    error: null,
    pagina: 0,
    paginas: 0,
    total: 0,
    cargandoMas: false,
    actuaciones: [],
  };
  void cargarPaginaExpansion(idProceso, 1);
}

async function cargarPaginaExpansion(idProceso: number, pagina: number): Promise<void> {
  const expansion = alta.expansion;
  if (!expansion || expansion.idProceso !== idProceso) return;
  if (pagina === 1) {
    expansion.loading = true;
    expansion.error = null;
    expansion.actuaciones = [];
    expansion.pagina = 0;
  } else {
    expansion.cargandoMas = true;
  }
  try {
    const respuesta = await api.consultarActuacionesPreview(idProceso, pagina);
    const actual = alta.expansion;
    if (actual !== expansion) return;
    actual.pagina = respuesta.pagina;
    actual.paginas = respuesta.paginas;
    actual.total = respuesta.total_actuaciones;
    actual.error = null;
    actual.actuaciones = pagina === 1
      ? respuesta.actuaciones
      : [...actual.actuaciones, ...respuesta.actuaciones];
  } catch (error) {
    const actual = alta.expansion;
    if (actual !== expansion) return;
    actual.error = api.mensajeError(error);
  } finally {
    const actual = alta.expansion;
    if (actual === expansion) {
      actual.loading = false;
      actual.cargandoMas = false;
    }
  }
}

export async function reintentarExpansionPreview(): Promise<void> {
  const expansion = alta.expansion;
  if (!expansion || expansion.loading || expansion.cargandoMas) return;
  expansion.error = null;
  await cargarPaginaExpansion(expansion.idProceso, expansion.pagina ? expansion.pagina + 1 : 1);
}

export function cargarMasActuacionesPreview(): void {
  const expansion = alta.expansion;
  if (!expansion || expansion.loading || expansion.cargandoMas) return;
  if (expansion.pagina >= expansion.paginas) return;
  void cargarPaginaExpansion(expansion.idProceso, expansion.pagina + 1);
}

// ── Seguir un proceso desde un resultado ────────────────────

export async function seguirProcesoPreview(
  radicadoDigitos: string,
  etiqueta: string,
): Promise<void> {
  if (!radicadoDigitos || alta.seguirBusy) return;
  const secuencia = ++secuenciaSeguir;
  alta.seguirBusy = true;
  alta.seguirError = null;
  try {
    const respuesta = await api.crearSeguimientoRadicado(
      radicadoDigitos,
      alta.correoActivo,
    );
    if (secuencia !== secuenciaSeguir) return;
    alta.seguido = respuesta;
    avisar(
      respuesta.creado
        ? `Seguimiento creado para el proceso ${etiqueta}.`
        : `Ya seguías el proceso ${etiqueta}.`,
      'success',
    );
  } catch (error) {
    if (secuencia !== secuenciaSeguir) return;
    alta.seguirError = api.mensajeError(error);
    avisar(`No se pudo seguir el proceso ${etiqueta}: ${alta.seguirError}`, 'error');
  } finally {
    if (secuencia === secuenciaSeguir) alta.seguirBusy = false;
  }
}
